import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, CircularProgress
} from '@mui/material';
import { notify } from '../notifications';
import {
  Logout, PeopleAlt, TableChart, FeaturedPlayList, ManageHistory, MarkAsUnread, MenuBook, CalendarMonth,
  AdminPanelSettings, Assignment, Analytics, Warehouse, Campaign as CampaignIcon, Today, ImportContacts,
  WarningAmber, History as HistoryIcon
} from '@mui/icons-material';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import DescriptionIcon from '@mui/icons-material/Description';
import PermContactCalendarIcon from '@mui/icons-material/PermContactCalendar';
import RequestPage from '@mui/icons-material/RequestPage';
import Blinds from '@mui/icons-material/Blinds';

import { useNavigate } from 'react-router-dom';
import axios from 'axios';

import AllUsers from './AllUsers/AllUsers';
import HelpPage from './HelpPage';
import Dangerous from './Dangerous';
import CheckUsersData from './reports/Reports';
import BDTable from './BDTable/BDTableMenu';
import ConsoleLog from './ConsoleLog';
import MathReport from './mathReports/MathReportMenu';
import TaskManager from './TaskManager';
import TabelRB from './TabelRB';
import EditReportModule from './reports/EditReportModule';
import EditMonthReportModule from './reports/EditMonthReportModule';
import ViewReportModule from './reports/ViewReportModule';
import ViewReportModuleDir from './reports/ViewReportModuleDir';
import UsersPerDay from './reports/CheckUsersOnDay';
import Compare from './reports/CompareModule';
import OFDManagement from './OFDManagement';
import Remains from './Remains';
import Nomenclature from './Nomenclature';
import AdminDocuments from './AdminDocuments';
import AdminMarkingCodes from './MarkingCodes';
import WhatsAppBots from './WhatsAppBots';
import AdminJournal from './AdminJournal';
import AdminNotifications from './Notifications';
import RoleManagement from './RoleManagement';
import AuditLog from './audit/AuditLog';
import QrCodeIcon from '@mui/icons-material/QrCode';
import HomeIcon from '@mui/icons-material/Home';
import HomePage from '../home/HomePage';
import { UserProfilePage, verifyAndSyncProfileStatus, getProfileBlockingMessage } from '../profile';
import { logoutCurrentAccount, useSessionActivity } from '../auth';
import { PROFILE_START_PAGE, findStartPage, getMissingStartPagePerms } from '../../config/startPages';
import AppSidebar, { SidebarSection } from '../layout/AppSidebar';
import AppHeader from '../layout/AppHeader';

type ReportModule = 'edit' | 'editmonth' | 'editnew' | 'view' | 'empty' | 'compare' | 'MathReport';

const AdminPanel: React.FC = () => {
  const role = localStorage.getItem('role');
  const username = localStorage.getItem('username')?.toLowerCase();

  const isSuperAdmin = role === 'superadmin';

  // Динамические права доступа текущей роли (PBAC)
  const [livePermissions, setLivePermissions] = useState<Record<string, string | boolean>>(() => {
    try {
      const raw = localStorage.getItem('permissions');
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });

  // Автоматическая фоновая синхронизация прав роли с бэкенда
  useEffect(() => {
    if (!role) return;
    axios.get(`/api/v2/roles/${role}`).then(res => {
      if (res.data?.success && res.data.role?.permissions) {
        setLivePermissions(res.data.role.permissions);
        localStorage.setItem('permissions', JSON.stringify(res.data.role.permissions));
      }
    }).catch(err => {
      console.warn('Не удалось обновить права роли:', err);
    });
  }, [role]);

  // Универсальный гейт прав: для superadmin всегда true, для остальных проверка прав
  const hasPerm = useCallback((permCode: string): boolean => {
    if (isSuperAdmin) return true;
    const val = livePermissions[permCode];
    return Boolean(val && val !== 'none' && val !== 'false');
  }, [isSuperAdmin, livePermissions]);

  const [selectedIndex, setSelectedIndex] = useState(99);
  const [selectedReportModule, setSelectedReportModule] = useState<ReportModule | undefined>(undefined);
  const [usernameState, setUsernameState] = useState('');
  const [currentRole, setCurrentRole] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Панель администратора | Zerde';
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab');
    const module = params.get('module') as ReportModule | null;

    if (localStorage.getItem('profileIncomplete') === 'true') {
      setSelectedIndex(99);
      return;
    }

    if (tab) {
      const tabNum = Number(tab);
      if (tabNum === 107 && !hasPerm('system.notifications.send')) {
        // Доступ к уведомлениям только при наличии права
      } else {
        setSelectedIndex(tabNum);
      }
    }
    if (module) {
      setSelectedReportModule(module);
    }
  }, [hasPerm]);

  const fetchUserRole = async (usernameToFetch: string) => {
    try {
      const response = await axios.get('/api/user-role', {
        params: { username: usernameToFetch }
      });
      if (response.data.success) {
        setCurrentRole(response.data.role);
      } else {
        notify.error('Ошибка получения роли: ' + response.data.message);
        setCurrentRole('');
      }
    } catch (error) {
      console.error('Ошибка при получении роли пользователя:', error);
      notify.error('Ошибка при получении данных');
      setCurrentRole('');
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  };

  const getAdminSectionTitle = (idx: number): string => {
    const titles: { [key: number]: string } = {
      0: 'Создать пользователя',
      2: 'Пользователи',
      3: 'Отчеты',
      4: 'Таблица',
      5: 'Логи',
      6: 'Мат. отчет',
      7: 'Пользователи',
      10: 'Остатки',
      11: 'Справочник',
      100: 'ОФД',
      101: 'WhatsApp боты',
      102: 'Сервер и службы',
      103: 'Документы (Накладные)',
      104: 'Номенклатура',
      105: 'Электронный журнал',
      106: 'Маркировка',
      107: 'Уведомления',
      108: 'Журнал действий',
      109: 'Карта аптек',
      110: 'Конструктор ролей',
      111: 'Журнал действий',
      200: 'Панель администратора',
      99: 'Профиль администратора'
    };
    return titles[idx] || `Раздел #${idx}`;
  };

  // Event-driven отслеживание активности и текущего раздела администратора
  useSessionActivity(getAdminSectionTitle(selectedIndex));

  const handleLogout = async () => {
    if (localStorage.getItem('profileIncomplete') === 'true') {
      notify.error(getProfileBlockingMessage('logout'));
      setSelectedIndex(99);
      return;
    }
    await logoutCurrentAccount();
    // Остальные аккаунты устройства покажет экран входа; в них он сам не входит
    navigate('/', { state: { skipAutoEnter: true } });
  };

  // Реакция на изменение статуса заполненности профиля
  useEffect(() => {
    const handleProfileStatus = () => {
      if (localStorage.getItem('profileIncomplete') === 'true') {
        setSelectedIndex(99);
      }
    };
    window.addEventListener('profileStatusChanged', handleProfileStatus);
    window.addEventListener('storage', handleProfileStatus);
    return () => {
      window.removeEventListener('profileStatusChanged', handleProfileStatus);
      window.removeEventListener('storage', handleProfileStatus);
    };
  }, []);

  // Принудительное удержание на странице профиля при незаполненных данных
  useEffect(() => {
    if (localStorage.getItem('profileIncomplete') === 'true' && selectedIndex !== 99) {
      setSelectedIndex(99);
    }
  }, [selectedIndex]);

  // Стартовая страница роли (задается в конструкторе ролей). Применяется при каждой загрузке панели
  // (вход, обновление страницы); профиль остается страницей по умолчанию и при незаполненном профиле / открытии по ссылке ?tab=
  const applyRoleStartPage = () => {
    setSelectedIndex(99);
    if (localStorage.getItem('profileIncomplete') === 'true') return;
    if (new URLSearchParams(window.location.search).get('tab')) return;

    const option = findStartPage('admin', localStorage.getItem('startPage'));
    if (!option || option.key === PROFILE_START_PAGE) return;
    if (getMissingStartPagePerms(option, hasPerm).length > 0) return;

    setSelectedIndex(option.index);
    if (option.module) {
      setSelectedReportModule(option.module as ReportModule);
    }
  };

  useEffect(() => {
    const storedUsername = localStorage.getItem('username');
    if (storedUsername) {
      setUsernameState(storedUsername);
      fetchUserRole(storedUsername);

      // Проверка обязательного заполнения профиля на сервере
      verifyAndSyncProfileStatus(storedUsername).then((isIncomplete) => {
        if (isIncomplete) {
          setSelectedIndex(99);
        }
      });

      // Одноразовые флаги входа больше не нужны: стартовая страница открывается при каждой загрузке панели
      sessionStorage.removeItem('openProfileOnLogin');
      localStorage.removeItem('openProfileOnLogin');
      applyRoleStartPage();
    } else {
      navigate('/');
    }
  }, [navigate]);

  if (initialLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Состав бокового меню. Видимость пунктов определяется только правами (PBAC, поле perms),
  // без привязки к ролям. Пункты синхронизированы с каталогом стартовых страниц (config/startPages.ts).
  const sidebarSections: SidebarSection[] = [
    {
      key: 'home',
      items: [
        { key: 'home', label: 'Главная', icon: <HomeIcon />, index: 20, perms: ['home.menu'] }
      ]
    },
    {
      key: 'reports',
      title: 'Отчетность',
      items: [
        { key: 'reports', label: 'Обзор', icon: <Analytics />, index: 3, perms: ['reports.menu'] },
        { key: 'reports.fill', label: 'Заполнение', icon: <PermContactCalendarIcon />, index: 3, module: 'empty', perms: ['reports.menu', 'reports.fill_monitoring'] },
        { key: 'reports.edit_daily', label: 'Редактирование', icon: <DriveFileRenameOutlineIcon />, index: 3, module: 'edit', perms: ['reports.menu', 'reports.edit_daily'] },
        { key: 'reports.edit_monthly', label: 'Ред. мес. отчет', icon: <CalendarMonth />, index: 3, module: 'editmonth', perms: ['reports.menu', 'reports.edit_monthly'] },
        { key: 'reports.summary', label: 'Отчет', icon: <DescriptionIcon />, index: 3, module: 'view', perms: ['reports.menu', 'reports.view_summary'] },
        { key: 'reports.director', label: 'Дир. отчет', icon: <Assignment />, index: 3, module: 'editnew', perms: ['reports.menu', 'reports.view_director'] },
        { key: 'reports.delivery', label: 'Сверка доставок', icon: <TableChart />, index: 3, module: 'compare', perms: ['reports.menu', 'reports.delivery.view'] },
        { key: 'reports.math', label: 'Мат. отчет', icon: <RequestPage />, index: 3, module: 'MathReport', perms: ['reports.menu', 'reports.math.view'] }
      ]
    },
    {
      key: 'documents',
      title: 'Документы',
      items: [
        { key: 'documents.invoices', label: 'Документы', icon: <DescriptionIcon />, index: 103, perms: ['documents.menu', 'documents.invoices.view'] },
        { key: 'documents.marking', label: 'КМ\'ы', icon: <QrCodeIcon />, index: 106, perms: ['documents.menu', 'documents.marking.view'] },
        { key: 'timesheet', label: 'Табель РБ', icon: <Today />, index: 101, perms: ['timesheet.menu'] },
        { key: 'journal', label: 'Электронный журнал', icon: <ImportContacts />, index: 105, perms: ['journal.menu', 'journal.view'] }
      ]
    },
    {
      key: 'data',
      title: 'Данные',
      items: [
        { key: 'guide.remains', label: 'Остатки', icon: <Warehouse />, index: 10, perms: ['guide.remains.menu', 'guide.remains.view'] },
        { key: 'guide.nomenclature', label: 'Справочник', icon: <MenuBook />, index: 11, perms: ['guide.nomenclature.menu', 'guide.nomenclature.view'] }
      ]
    },
    {
      key: 'administration',
      title: 'Администрирование',
      items: [
        { key: 'users', label: 'Пользователи', icon: <PeopleAlt />, index: 2, perms: ['users.menu'] },
        { key: 'tables', label: 'Таблица', icon: <TableChart />, index: 4, perms: ['tables.menu'] },
        { key: 'system.roles', label: 'Конструктор ролей', icon: <AdminPanelSettings />, index: 110, perms: ['system.menu', 'system.roles.manage'] },
        { key: 'system.notifications', label: 'Уведомления', icon: <CampaignIcon />, index: 107, perms: ['system.menu', 'system.notifications.send'] },
        { key: 'system.tasks', label: 'Планировщик', icon: <ManageHistory />, index: 7, perms: ['system.menu', 'system.tasks.manage'] },
        { key: 'system.whatsapp', label: 'WhatsApp боты', icon: <MarkAsUnread />, index: 104, perms: ['system.menu', 'system.whatsapp.manage'] },
        { key: 'system.ofd', label: 'Данные ОФД', icon: <Blinds />, index: 102, perms: ['system.menu'] },
        { key: 'logs', label: 'Логи', icon: <FeaturedPlayList />, index: 5, perms: ['system.logs.view'] },
        { key: 'audit', label: 'Журнал действий', icon: <HistoryIcon />, index: 111, perms: ['audit.menu', 'audit.view'] },
        { key: 'system.dangerous', label: 'Опасные функции', icon: <WarningAmber />, index: 100, perms: ['system.menu', 'system.dangerous.execute'] }
      ]
    }
  ];

  // Централизованный защищенный переход по вкладкам
  const navigateToTab = (index: number, module?: string) => {
    if (localStorage.getItem('profileIncomplete') === 'true' && index !== 99) {
      notify.error(getProfileBlockingMessage('navigate'));
      setSelectedIndex(99);
      return;
    }
    setSelectedIndex(index);
    if (module !== undefined) {
      setSelectedReportModule(module as ReportModule);
    }
  };

  const handleOpenInNewTab = (index: number, module?: string) => {
    if (localStorage.getItem('profileIncomplete') === 'true' && index !== 99) {
      notify.error(getProfileBlockingMessage('navigate'));
      return;
    }
    const url = window.location.href.split('?')[0] + `?tab=${index}${module ? `&module=${module}` : ''}`;
    window.open(url, '_blank');
  };

  // Внешние сервисы (почта, база знаний) недоступны, пока не заполнен профиль
  const openExternalService = (url: string) => {
    if (localStorage.getItem('profileIncomplete') === 'true') {
      notify.error(getProfileBlockingMessage('services'));
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const getPageTitle = () => {
    if (selectedIndex === 3 && selectedReportModule === 'MathReport') {
      return 'Мат. отчет';
    }

    switch (selectedIndex) {
      case 2:
        return 'Пользователи';
      case 3:
        return 'Отчеты';
      case 4:
        return 'Таблица';
      case 5:
        return 'Логи';
      case 6:
        return 'Мат. отчет';
      case 7:
        return 'Планировщик';
      case 10:
        return 'Остатки';
      case 11:
        return 'Справочник';
      case 20:
        return 'Главная';
      case 100:
        return 'Опасные функции';
      case 101:
        return 'Табель РБ';
      case 102:
        return 'Данные ОФД';
      case 103:
        return 'Документы';
      case 104:
        return 'WhatsApp Боты';
      case 105:
        return 'Электронный журнал';
      case 106:
        return 'КМ\'ы';
      case 107:
        return 'Центр уведомлений';
      case 110:
        return 'Конструктор ролей и прав доступа';
      case 111:
        return 'Журнал действий';
      case 99:
        return 'Профиль пользователя';
      default:
        return '';
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'white', overflow: 'hidden' }}>
      <AppSidebar
        id="admin"
        sections={sidebarSections}
        hasPerm={hasPerm}
        selectedIndex={selectedIndex}
        selectedModule={selectedIndex === 3 ? selectedReportModule : undefined}
        onSelect={navigateToTab}
        onOpenInNewTab={handleOpenInNewTab}
        footerActions={[
          {
            key: 'mail',
            label: 'Почта',
            icon: <MarkAsUnread />,
            iconOnly: true,
            onClick: () => openExternalService('https://mail.zerdegroup.kz/')
          },
          {
            key: 'guide',
            label: 'База знаний',
            icon: <MenuBook />,
            iconOnly: true,
            onClick: () => openExternalService('https://guide.zerdegroup.kz/')
          },
          { key: 'logout', label: 'Выйти', icon: <Logout />, onClick: handleLogout, danger: true }
        ]}
      />

      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
        <AppHeader
          title={getPageTitle()}
          username={usernameState || username}
          onProfileClick={() => navigateToTab(99)}
          isProfileSelected={selectedIndex === 99}
        />

        <Box sx={{
          flexGrow: 1,
          p: 4,
          overflow: 'auto',
          bgcolor: '#ffffff',
          minHeight: 0
        }}>
            {selectedIndex === 20 && hasPerm('home.menu') && <HomePage onNavigate={navigateToTab} />}
            {selectedIndex === 2 && hasPerm('users.menu') && <AllUsers />}
            {selectedIndex === 3 && hasPerm('reports.menu') && (
              <>
                {!selectedReportModule && <CheckUsersData setSelectedIndex={setSelectedIndex} />}
                {selectedReportModule === 'empty' && hasPerm('reports.fill_monitoring') && <UsersPerDay />}
                {selectedReportModule === 'edit' && hasPerm('reports.edit_daily') && <EditReportModule />}
                {selectedReportModule === 'editmonth' && hasPerm('reports.edit_monthly') && <EditMonthReportModule />}
                {selectedReportModule === 'editnew' && hasPerm('reports.view_director') && <ViewReportModuleDir />}
                {selectedReportModule === 'view' && hasPerm('reports.view_summary') && <ViewReportModule />}
                {selectedReportModule === 'compare' && hasPerm('reports.delivery.view') && <Compare />}
                {selectedReportModule === 'MathReport' && hasPerm('reports.math.view') && <MathReport setSelectedIndex={setSelectedIndex} />}
              </>
            )}
            {selectedIndex === 4 && (hasPerm('tables.menu') || hasPerm('system.db_table.manage')) && <BDTable setSelectedIndex={setSelectedIndex} />}
            {selectedIndex === 5 && hasPerm('system.logs.view') && <ConsoleLog />}
            {selectedIndex === 6 && hasPerm('reports.math.view') && <MathReport setSelectedIndex={setSelectedIndex} />}
            {selectedIndex === 7 && hasPerm('system.tasks.manage') && <TaskManager />}
            {selectedIndex === 10 && hasPerm('guide.remains.view') && <Remains />}
            {selectedIndex === 11 && hasPerm('guide.nomenclature.view') && <Nomenclature />}
            {selectedIndex === 100 && hasPerm('system.dangerous.execute') && <Dangerous />}
            {selectedIndex === 101 && hasPerm('timesheet.menu') && <TabelRB />}
            {selectedIndex === 102 && hasPerm('system.menu') && <OFDManagement />}
            {selectedIndex === 103 && hasPerm('documents.invoices.view') && <AdminDocuments />}
            {selectedIndex === 104 && hasPerm('system.whatsapp.manage') && <WhatsAppBots />}
            {selectedIndex === 105 && hasPerm('journal.menu') && hasPerm('journal.view') && <AdminJournal />}
            {selectedIndex === 106 && hasPerm('documents.marking.view') && <AdminMarkingCodes />}
            {selectedIndex === 107 && hasPerm('system.notifications.send') && <AdminNotifications />}
            {selectedIndex === 110 && hasPerm('system.roles.manage') && <RoleManagement />}
            {selectedIndex === 111 && hasPerm('audit.menu') && hasPerm('audit.view') && <AuditLog />}
            {selectedIndex === 99 && (
              <UserProfilePage
                username={usernameState || username || ''}
                pharmacyNum={null}
                userRole={currentRole || role}
              />
            )}
        </Box>
      </Box>
    </Box>
  );
};

export default AdminPanel;
